// My bookings — list of the customer's own reservations, grouped upcoming/past.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { customerApi } from "@/lib/customerAuth";
import { CalendarDays, DollarSign, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const usd = (n) => `$${Number(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

const STATUS_COLOR = {
    pending: "bg-amber-100 text-amber-800",
    confirmed: "bg-jade/15 text-jade",
    checked_in: "bg-navy text-ivory",
    checked_out: "bg-navy/20 text-navy",
    cancelled: "bg-red-100 text-red-700",
    no_show: "bg-neutral-200 text-neutral-600",
};

export function AccountBookings() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        customerApi.get("/customer/reservations")
            .then((r) => setRows(r.data))
            .catch(() => toast.error("Could not load your bookings"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-navy/60 text-sm">Loading your bookings…</div>;

    const today = new Date().toISOString().slice(0, 10);
    const upcoming = rows.filter((r) => r.check_in >= today && !["cancelled", "no_show", "checked_out"].includes(r.status));
    const past = rows.filter((r) => !upcoming.includes(r));

    return (
        <div className="space-y-8" data-testid="account-bookings">
            <h2 className="font-display text-3xl text-navy">My bookings</h2>

            <Section title="Upcoming" rows={upcoming} empty="No upcoming stays." />
            <Section title="Past & cancelled" rows={past} empty="No past bookings yet." />
        </div>
    );
}

function Section({ title, rows, empty }) {
    return (
        <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-3">{title}</div>
            {rows.length === 0 ? (
                <div className="bg-white border border-navy/10 rounded-2xl p-6 text-navy/50 text-sm">{empty}</div>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <Link key={r.id} to={`/account/bookings/${r.id}`} data-testid={`bkg-${r.id}`} className="block bg-white border border-navy/10 rounded-2xl p-5 hover:border-gold/50 transition-colors">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{r.confirmation_number}</div>
                                    <div className="font-display text-xl text-navy mt-1">{r.room_name || r.room_slug}</div>
                                    <div className="text-navy/60 text-sm mt-1 flex items-center gap-2">
                                        <CalendarDays size={13} className="text-jade" /> {r.check_in} → {r.check_out} · {r.nights} nights
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className={`text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full inline-block ${STATUS_COLOR[r.status] || "bg-navy/10 text-navy"}`}>{r.status?.replace("_", " ")}</div>
                                    <div className="mt-2 font-display text-lg text-navy inline-flex items-center gap-1">
                                        <DollarSign size={13} className="text-gold" />{usd(r.total).replace("$", "")}
                                    </div>
                                    {r.remaining_balance > 0 && <div className="text-[11px] text-amber-700 mt-1">Balance due: {usd(r.remaining_balance)}</div>}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </section>
    );
}

export function AccountBookingDetail() {
    const { id } = useParams();
    const [r, setR] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        customerApi.get(`/customer/reservations/${id}`)
            .then((res) => setR(res.data))
            .catch(() => toast.error("Could not load booking"))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="text-navy/60 text-sm">Loading booking…</div>;
    if (!r) return (
        <div>
            <Link to="/account/bookings" className="text-sm text-gold hover:underline"><ArrowLeft size={13} className="inline mr-1" />Back to bookings</Link>
            <div className="mt-4 text-navy/60">Not found.</div>
        </div>
    );

    return (
        <div className="space-y-6" data-testid="account-booking-detail">
            <Link to="/account/bookings" className="text-sm text-gold hover:underline"><ArrowLeft size={13} className="inline mr-1" />Back to bookings</Link>
            <div className="bg-white border border-navy/10 rounded-2xl p-8">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{r.confirmation_number}</div>
                <h2 className="font-display text-3xl text-navy mt-2">{r.room_name || r.room_slug}</h2>
                <div className="mt-2 text-navy/70 text-sm">{r.check_in} → {r.check_out} · {r.nights} nights · {r.adults} adult{r.adults > 1 ? "s" : ""}{r.children ? `, ${r.children} children` : ""}</div>
                <div className={`inline-block mt-3 text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${STATUS_COLOR[r.status] || "bg-navy/10 text-navy"}`}>{r.status?.replace("_", " ")}</div>

                <div className="mt-8 grid md:grid-cols-2 gap-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-3">Charges</div>
                        <Row k="Room subtotal" v={usd(r.subtotal)} />
                        <Row k="Taxes & fees" v={usd(r.taxes_fees)} />
                        {r.discount_amount > 0 && <Row k="Discount" v={`-${usd(r.discount_amount)}`} />}
                        <Row k="Total" v={usd(r.total)} bold />
                        <Row k="Paid" v={usd(r.amount_paid)} />
                        {r.remaining_balance > 0 && <Row k="Balance due" v={usd(r.remaining_balance)} bold />}
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-3">Contact</div>
                        <Row k="Guest" v={r.guest_name} />
                        <Row k="Email" v={r.email} />
                        {r.phone && <Row k="Phone" v={r.phone} />}
                        {r.country && <Row k="Country" v={r.country} />}
                        {r.special_requests && <div className="mt-4">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Special requests</div>
                            <div className="text-navy/80 text-sm mt-1">{r.special_requests}</div>
                        </div>}
                    </div>
                </div>

                <div className="mt-8 text-[11px] text-navy/50">
                    Need to make a change? <a href="mailto:stay@serenitycoveresorts.com" className="text-gold hover:underline">Email the concierge team</a> or call +1 (876) 634-7439.
                </div>
            </div>
        </div>
    );
}

function Row({ k, v, bold }) {
    return (
        <div className={`flex items-center justify-between py-2 border-b border-navy/5 text-sm ${bold ? "font-medium text-navy" : "text-navy/70"}`}>
            <span>{k}</span>
            <span className={bold ? "font-display text-navy" : ""}>{v}</span>
        </div>
    );
}
